import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { payoutFilters } from './AdminPayoutsScreen.css'
import { useState } from 'react'
import { Landmark } from 'lucide-react'
import {
  Banner,
  Button,
  Card,
  Drawer,
  DrawerSection,
  EmptyState,
  ErrorState,
  FilterToolbar,
  KeyValueGrid,
  LoadMore,
  MoneyValue,
  SelectField,
  Skeleton,
  StatusBadge,
  TextField,
} from '@shared/design-system/ui'
import { hasAdminPermission } from '@shared/lib/auth/permissions'
import { useSession } from '@shared/lib/auth/session-store'
import { useMediaQuery } from '@shared/lib/use-media-query'
import { getPayoutFailureMessage } from '@shared/lib/errors/user-facing-error'
import { formatBankDate } from '../lib/bank-date'
import {
  useRefreshAdminReceipts,
  payoutIdentity,
  useAdminPayouts,
  useAdminPayout,
  useAdminPayoutComposition,
} from '../api/admin-payouts'
import type { ConnectPayout, ConnectMovement } from '../api/connect-receipts.contract'
import { AdminUserSearchField } from './AdminUserSearchField'
import { useResolvedAdminUser } from './useResolvedAdminUser'
import {
  page,
  header,
  titleBlock,
  title,
  subtitle,
  tableCard,
  tableViewport,
  table,
  th,
  td,
  mobileList,
  mobileCard,
  mobileMetaGrid,
  mobileMetaItem,
  mobileMetaLabel,
  mobileMetaValue,
  muted,
} from './admin.css'

const payoutStatuses = {
  pending: { label: 'Pendente', tone: 'awaiting' },
  in_transit: { label: 'Em transferência bancária', tone: 'informative' },
  paid: { label: 'Pago pela Stripe', tone: 'success' },
  failed: { label: 'Falhou', tone: 'closed' },
  canceled: { label: 'Cancelado', tone: 'neutral' },
} as const
const compositionLabels = {
  pending: 'Composição pendente',
  complete: 'Composição confirmada',
  unavailable: 'Composição indisponível',
  divergent: 'Composição divergente',
}
const ledgerStatuses = {
  pending: { label: 'Conciliação financeira pendente', tone: 'awaiting' },
  completed: { label: 'Efeito financeiro aplicado', tone: 'success' },
  reversed: { label: 'Efeito financeiro revertido', tone: 'neutral' },
} as const
const ledgerReasons = {
  composition_pending: 'A composição Stripe ainda não foi confirmada.',
  composition_divergent: 'A composição Stripe apresenta divergências que precisam ser conciliadas.',
  allocation_or_eligibility_unproven:
    'As referências dos recebíveis ou sua elegibilidade, incluindo a reserva comercial, ainda não foram comprovadas.',
} as const
function LedgerNotice({ payout }: { payout: ConnectPayout }) {
  return payout.ledgerStatus === 'pending' ? (
    <Banner tone="warning" title={ledgerStatuses.pending.label}>
      {payout.ledgerPendingReason
        ? ledgerReasons[payout.ledgerPendingReason]
        : 'A conciliação ainda não foi concluída.'}{' '}
      A observação da Stripe não confirma efeito financeiro aplicado ao saldo nem confirmação
      bancária.
    </Banner>
  ) : (
    <StatusBadge {...ledgerStatuses[payout.ledgerStatus]} />
  )
}
function legacyDateLabel(value: string | null) {
  return value ? new Date(value).toLocaleString('pt-BR') : 'Não informada'
}
function useReceiptDate() {
  const enabled = useFeatureFlag('receiptsExperience')
  return (value: string | null) =>
    enabled && value
      ? new Intl.DateTimeFormat('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'short',
          timeZone: 'America/Sao_Paulo',
        }).format(new Date(value)) + ' (Brasília)'
      : legacyDateLabel(value)
}
function bankLabel(payout: ConnectPayout) {
  return payout.destination
    ? `${payout.destination.bankName ?? 'Banco não informado'} · ${payout.destination.last4 ? `•••• ${payout.destination.last4}` : 'Final não informado'}`
    : 'Banco não informado'
}
function SyncNotice({
  data,
  compact = false,
}: {
  data: { lastSyncedAt: string | null; syncStatus: string }
  compact?: boolean
}) {
  const dateLabel = useReceiptDate()
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  if (data.syncStatus === 'current')
    return (
      <p className={muted}>
        Atualizado em {dateLabel(data.lastSyncedAt)}.{' '}
        {receiptsExperience
          ? 'Use Atualizar para consultar novamente.'
          : 'Consulta automática a cada 15 segundos.'}
      </p>
    )
  const labels: Record<string, string> = {
    pending: 'Aguardando sincronização',
    stale: 'Dados atrasados',
    unavailable: 'Dados indisponíveis',
  }
  if (compact) return <p className={muted}>{labels[data.syncStatus]}</p>
  return (
    <Banner
      tone={data.syncStatus === 'unavailable' ? 'danger' : 'warning'}
      title={labels[data.syncStatus]}
    >
      {data.lastSyncedAt
        ? `Última leitura em ${dateLabel(data.lastSyncedAt)}. Os dados anteriores continuam visíveis.`
        : 'Ainda não há uma leitura confirmada. Aguarde ou atualize a consulta.'}
    </Banner>
  )
}
function Movement({ item }: { item: ConnectMovement }) {
  const dateLabel = useReceiptDate()
  return (
    <Card padding="sm">
      <KeyValueGrid
        items={[
          { label: 'Movimentação', value: item.id },
          { label: 'Tipo', value: item.reportingCategory ?? item.type },
          { label: 'Valor', value: <MoneyValue {...item.amount} /> },
          { label: 'Tarifa informada', value: <MoneyValue {...item.fee} /> },
          { label: 'Líquido', value: <MoneyValue {...item.net} /> },
          {
            label: 'Disponibilidade',
            value: item.status === 'available' ? 'Disponível' : 'Pendente',
          },
          { label: 'Criada em', value: dateLabel(item.createdAt) },
          { label: 'Disponível em', value: dateLabel(item.availableOn) },
          {
            label: 'Vendas vinculadas',
            value: item.saleIds.length
              ? item.saleIds.join(', ')
              : 'Vínculo com venda não informado',
          },
        ]}
      />
    </Card>
  )
}
function PayoutDetail({ selected, onClose }: { selected: ConnectPayout; onClose: () => void }) {
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  const refresh = useRefreshAdminReceipts()
  const canReadUsers = hasAdminPermission(
    useSession((s) => s.user),
    'USER_READ',
  )
  const dateLabel = useReceiptDate()
  const [compositionOpen, setCompositionOpen] = useState(false)
  const showComposition = !receiptsExperience || compositionOpen
  const detail = useAdminPayout(selected)
  const composition = useAdminPayoutComposition(selected, showComposition)
  const payout = detail.data
  const movements = composition.data?.pages.flatMap((p) => p.items) ?? []
  const compositionPage = composition.data?.pages[0]
  return (
    <Drawer open onClose={onClose} title="Detalhes do recebimento" variant="financial" size="wide">
      <div data-testid="admin-payout-detail" />
      {receiptsExperience ? (
        <Button
          data-testid="admin-payout-refresh-btn"
          variant="secondary"
          disabled={detail.isFetching || composition.isFetching}
          onClick={() => {
            void refresh()
          }}
        >
          Atualizar consulta
        </Button>
      ) : null}
      {detail.isError ? (
        <ErrorState
          title="Não foi possível atualizar o recebimento"
          description="Tente novamente para consultar o estado informado pela Stripe."
          onRetry={() => void detail.refetch()}
        />
      ) : null}
      {!payout && detail.isPending ? <Skeleton height={280} /> : null}
      {payout ? (
        <>
          <StatusBadge {...payoutStatuses[payout.status]} />
          <LedgerNotice payout={payout} />
          <SyncNotice data={payout} />
          {payout.status === 'failed' || payout.failure ? (
            <Banner tone="danger" title="O recebimento apresentou uma falha">
              {getPayoutFailureMessage(payout.failure?.code)} Atualize a consulta para acompanhar a
              correção.
            </Banner>
          ) : null}
          <DrawerSection
            title="Recebimento bancário"
            description="A Stripe informa o pagamento. Este estado não é uma confirmação bancária independente."
          >
            <KeyValueGrid
              items={[
                { label: 'Valor', value: <MoneyValue {...payout.amount} /> },
                { label: 'Participante', value: payout.participant.name ?? payout.participant.id },
                ...(receiptsExperience && canReadUsers
                  ? [{ label: 'E-mail', value: payout.participant.email ?? 'Não informado' }]
                  : []),
                { label: 'Identificador', value: payout.id },
                { label: 'Criado em', value: dateLabel(payout.createdAt) },
                { label: 'Previsão bancária', value: formatBankDate(payout.arrivalDate) },
                { label: 'Banco original', value: bankLabel(payout) },
                { label: 'Conta Connect', value: payout.account.accountId },
                { label: 'Plataforma', value: payout.account.platformId },
                { label: 'Ambiente', value: payout.account.livemode ? 'Produção' : 'Teste' },
              ]}
            />
          </DrawerSection>
          {receiptsExperience ? (
            <Button
              data-testid="admin-payout-composition-toggle"
              variant="secondary"
              aria-expanded={compositionOpen}
              onClick={() => setCompositionOpen((open) => !open)}
            >
              {compositionOpen ? 'Ocultar composição' : 'Ver composição'}
            </Button>
          ) : null}
          {showComposition ? (
            <DrawerSection
              title="Composição"
              description="Movimentações vinculadas pela Stripe. Valores carregados são parciais enquanto houver mais páginas."
            >
              {compositionPage ? (
                <>
                  <StatusBadge
                    label={compositionLabels[compositionPage.compositionStatus]}
                    tone={compositionPage.compositionStatus === 'divergent' ? 'closed' : 'neutral'}
                  />
                  <SyncNotice data={compositionPage} />
                </>
              ) : null}
              {composition.isError ? (
                <ErrorState
                  title="Não foi possível carregar a composição"
                  onRetry={() => void composition.refetch()}
                />
              ) : null}
              {composition.isFetchNextPageError ? (
                <ErrorState
                  title="Não foi possível carregar mais movimentações"
                  onRetry={() => void composition.fetchNextPage()}
                />
              ) : null}
              {composition.isPending ? <Skeleton height={180} /> : null}
              {movements.map((item) => (
                <Movement
                  key={`${item.account.platformId}:${item.account.accountId}:${item.account.livemode}:${item.id}`}
                  item={item}
                />
              ))}
              {compositionPage?.compositionStatus === 'complete' &&
              compositionPage.syncStatus === 'current' &&
              movements.length === 0 ? (
                <p>Nenhuma movimentação vinculada.</p>
              ) : null}
              <LoadMore
                shown={movements.length}
                hasMore={composition.hasNextPage}
                loading={composition.isFetchingNextPage}
                onClick={() => void composition.fetchNextPage()}
              />
            </DrawerSection>
          ) : null}
        </>
      ) : null}
    </Drawer>
  )
}
export function AdminWithdrawalsScreen() {
  const refresh = useRefreshAdminReceipts()
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  const dateLabel = useReceiptDate()
  const user = useSession((s) => s.user)
  const allowed = hasAdminPermission(user, 'PAYOUT_READ')
  const canReadUsers = allowed && hasAdminPermission(user, 'USER_READ')
  const showEmail = receiptsExperience && canReadUsers
  const [q, setQ] = useState('')
  const [userQuery, setUserQuery] = useState('')
  const userFilter = useResolvedAdminUser(canReadUsers ? userQuery : '')
  const userId = canReadUsers ? userFilter.resolvedUserId : ''
  const unresolvedUser = canReadUsers && Boolean(userQuery.trim()) && !userId
  const [status, setStatus] = useState<ConnectPayout['status'] | ''>('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [selected, setSelected] = useState<ConnectPayout | null>(null)
  const mobile = useMediaQuery('(max-width: 720px)')
  const invalidDates = Boolean(dateFrom && dateTo && dateFrom > dateTo)
  const payouts = useAdminPayouts(
    {
      q: q.trim() || undefined,
      userId: userId.trim() || undefined,
      status: status || undefined,
      dateFrom: dateFrom ? new Date(`${dateFrom}T00:00:00-03:00`).toISOString() : undefined,
      dateTo: dateTo ? new Date(`${dateTo}T23:59:59.999-03:00`).toISOString() : undefined,
    },
    allowed && !invalidDates && !unresolvedUser,
  )
  const items = unresolvedUser ? [] : (payouts.data?.pages.flatMap((p) => p.items) ?? [])
  const firstPage = unresolvedUser ? undefined : payouts.data?.pages[0]
  if (!allowed)
    return (
      <Banner tone="warning" title="Acesso restrito">
        Solicite acesso à equipe responsável para consultar recebimentos.
      </Banner>
    )
  return (
    <section className={page} data-testid="withdrawals-screen">
      <div className={header}>
        <div className={titleBlock}>
          <h1 className={title}>Recebimentos</h1>
          <p className={subtitle}>
            Acompanhe os pagamentos bancários automáticos informados pela Stripe.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => void (receiptsExperience ? refresh() : payouts.refetch())}
          disabled={invalidDates || unresolvedUser || payouts.isFetching}
        >
          Atualizar
        </Button>
      </div>
      <Card padding="md" className={tableCard}>
        <FilterToolbar
          onClear={
            q || userQuery || status || dateFrom || dateTo
              ? () => {
                  setQ('')
                  setUserQuery('')
                  setStatus('')
                  setDateFrom('')
                  setDateTo('')
                }
              : undefined
          }
          resultCount={
            <span>
              {firstPage?.lastSyncedAt
                ? `${items.length} ${items.length === 1 ? 'recebimento carregado' : 'recebimentos carregados'}`
                : 'Consulta de recebimentos'}
            </span>
          }
        >
          <div className={payoutFilters}>
            <TextField
              label="Buscar por ID"
              placeholder="ID do participante, conta Connect ou recebimento"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {canReadUsers ? (
              <AdminUserSearchField
                value={userQuery}
                onChange={setUserQuery}
                state={userFilter}
                placeholder="Nome, e-mail ou ID do usuário"
              />
            ) : null}
            <TextField
              label="Criado a partir de"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <TextField
              label="Criado até"
              type="date"
              value={dateTo}
              error={
                invalidDates ? 'A data final deve ser igual ou posterior à inicial.' : undefined
              }
              onChange={(e) => setDateTo(e.target.value)}
            />
            <SelectField
              label="Estado do recebimento"
              value={status}
              options={[
                { value: '', label: 'Todos os estados' },
                ...Object.entries(payoutStatuses).map(([value, data]) => ({
                  value,
                  label: data.label,
                })),
              ]}
              onChange={(e) => setStatus(e.target.value as ConnectPayout['status'] | '')}
            />
          </div>
        </FilterToolbar>
        {firstPage ? <SyncNotice data={firstPage} /> : null}
        {!unresolvedUser && payouts.isError ? (
          <ErrorState
            title="Não foi possível carregar os recebimentos"
            description="Tente novamente para consultar os pagamentos bancários."
            onRetry={() => void payouts.refetch()}
          />
        ) : null}
        {unresolvedUser ? (
          <Banner tone="info" title="Selecione um usuário">
            Localize e selecione o usuário para consultar seus recebimentos.
          </Banner>
        ) : null}
        {!invalidDates && !unresolvedUser && payouts.isPending ? <Skeleton height={320} /> : null}
        {firstPage &&
        items.length === 0 &&
        firstPage.lastSyncedAt &&
        firstPage.syncStatus === 'current' ? (
          <EmptyState
            icon={<Landmark size={22} />}
            title="Nenhum recebimento encontrado"
            description="Ajuste os filtros para consultar outro período ou participante."
          />
        ) : null}
        {items.length > 0 ? (
          mobile ? (
            <div className={mobileList}>
              {items.map((item) => (
                <Card key={payoutIdentity(item)} className={mobileCard} padding="md">
                  <strong>{item.participant.name ?? item.participant.id}</strong>
                  <span className={muted}>
                    {item.id} · {item.account.accountId} ·{' '}
                    {item.account.livemode ? 'Produção' : 'Teste'}
                  </span>
                  <StatusBadge {...payoutStatuses[item.status]} />
                  <StatusBadge {...ledgerStatuses[item.ledgerStatus]} />
                  {item.syncStatus !== 'current' ? <SyncNotice data={item} compact /> : null}
                  <MoneyValue {...item.amount} />
                  <div className={mobileMetaGrid}>
                    {[
                      ...(showEmail ? [['E-mail', item.participant.email ?? 'Não informado']] : []),
                      ['Banco original', bankLabel(item)],
                      ['Previsão bancária', formatBankDate(item.arrivalDate)],
                      ['Criado em', dateLabel(item.createdAt)],
                    ].map(([label, value]) => (
                      <div key={label} className={mobileMetaItem}>
                        <span className={mobileMetaLabel}>{label}</span>
                        <span className={mobileMetaValue}>{value}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => setSelected(item)}
                    aria-label={`Ver detalhes do recebimento ${item.id}`}
                  >
                    Detalhes
                  </Button>
                </Card>
              ))}
            </div>
          ) : (
            <div className={tableViewport}>
              <table className={table} data-testid="admin-payouts-table">
                <thead>
                  <tr>
                    {[
                      'Participante',
                      ...(showEmail ? ['E-mail'] : []),
                      'Valor',
                      'Estado',
                      'Banco original',
                      'Criado em',
                      'Previsão bancária',
                      '',
                    ].map((label) => (
                      <th key={label} className={th}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={payoutIdentity(item)}>
                      <td className={td}>
                        <div>{item.participant.name ?? item.participant.id}</div>
                        <span className={muted}>
                          {item.id} · {item.account.accountId} ·{' '}
                          {item.account.livemode ? 'Produção' : 'Teste'}
                        </span>
                      </td>
                      {showEmail ? (
                        <td className={td} data-testid={`payout-email-${item.id}`}>
                          {item.participant.email ?? 'Não informado'}
                        </td>
                      ) : null}
                      <td className={td}>
                        <MoneyValue {...item.amount} />
                      </td>
                      <td className={td}>
                        <StatusBadge {...payoutStatuses[item.status]} />
                        <StatusBadge {...ledgerStatuses[item.ledgerStatus]} />
                        {item.syncStatus !== 'current' ? <SyncNotice data={item} compact /> : null}
                      </td>
                      <td className={td}>{bankLabel(item)}</td>
                      <td className={td}>{dateLabel(item.createdAt)}</td>
                      <td className={td}>{formatBankDate(item.arrivalDate)}</td>
                      <td className={td}>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setSelected(item)}
                          aria-label={`Ver detalhes do recebimento ${item.id}`}
                        >
                          Detalhes
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
        {!unresolvedUser && payouts.isFetchNextPageError ? (
          <ErrorState
            title="Não foi possível carregar mais recebimentos"
            onRetry={() => void payouts.fetchNextPage()}
          />
        ) : null}
        {items.length ? (
          <LoadMore
            shown={items.length}
            hasMore={payouts.hasNextPage}
            loading={payouts.isFetchingNextPage}
            onClick={() => void payouts.fetchNextPage()}
          />
        ) : null}
      </Card>
      {selected ? (
        <PayoutDetail
          key={payoutIdentity(selected)}
          selected={selected}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </section>
  )
}
